// Console information architecture: PowerBESS is one console hosting an
// executive Overview plus four products (Trade, Comply, Health, Data) and a
// Manage area (organization + account settings). This file is the single
// source of truth for the app launcher, the product-aware sidebar, the
// command palette, and active-app detection.

import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BadgeCheck,
  BarChart3,
  BatteryCharging,
  BatteryMedium,
  Bell,
  Building2,
  CandlestickChart,
  ClipboardList,
  Cpu,
  Database,
  FileText,
  Flag,
  FlaskConical,
  Gauge,
  Gavel,
  HeartPulse,
  IndianRupee,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  MonitorPlay,
  Network,
  Scale,
  ScrollText,
  Siren,
  TrendingUp,
  UserCog,
  Users,
  Wrench,
  Waypoints,
} from "lucide-react";

export type AppId = "overview" | "trade" | "comply" | "asset" | "health" | "data" | "manage";

export interface ConsoleNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Any-of role gate. Empty/undefined = any authenticated user. */
  roles?: string[];
  /** Scaffolded but not implemented — renders a disabled "soon" item. */
  soon?: boolean;
  /** Exact-match the route for the active state (used for app home routes). */
  end?: boolean;
  /** Short hint shown in the command palette. */
  hint?: string;
  /** Product accent — renders the icon as that product's coloured tile. */
  accent?: string;
}

export interface ConsoleNavGroup {
  id: string;
  label: string;
  items: ConsoleNavItem[];
}

export interface ConsoleApp {
  id: AppId;
  /** Short name shown in the launcher tile and sidebar header. */
  name: string;
  /** Marketing name, e.g. "PowerBESS Trade". */
  fullName: string;
  tagline: string;
  icon: LucideIcon;
  /** Accent hex used for the launcher tile + sidebar header icon. */
  accent: string;
  /** Where the launcher tile navigates. */
  home: string;
  /** Route prefixes this app owns — drives active-app detection. */
  owns: string[];
  kind: "product" | "manage";
  /** Any-of role gate to even see the app in the launcher. */
  roles?: string[];
  groups: ConsoleNavGroup[];
}

// ---------------------------------------------------------------------------
// App definitions
// ---------------------------------------------------------------------------

const OVERVIEW: ConsoleApp = {
  id: "overview",
  name: "Overview",
  fullName: "PowerBESS Overview",
  tagline: "Your products at a glance",
  icon: Gauge,
  accent: "#2dd4bf",
  home: "/overview",
  owns: ["/overview"],
  kind: "product",
  groups: [
    {
      id: "executive",
      label: "Platform",
      items: [
        { to: "/overview", label: "Overview", icon: Gauge, end: true, hint: "Launcher — one live number per product" },
      ],
    },
  ],
};

const TRADE: ConsoleApp = {
  id: "trade",
  name: "Trade",
  fullName: "PowerBESS Trade",
  tagline: "Forecast, analyse & advise",
  icon: TrendingUp,
  accent: "#CCFF00",
  home: "/trade",
  owns: ["/trade"],
  kind: "product",
  groups: [
    {
      id: "overview",
      label: "Overview",
      items: [
        { to: "/trade", label: "Trading desk", icon: LayoutDashboard, end: true, hint: "Fleet & market KPIs" },
      ],
    },
    {
      id: "markets",
      label: "Markets",
      items: [
        { to: "/trade/market", label: "Market", icon: CandlestickChart, hint: "RTM/DAM prices" },
        { to: "/trade/forecasts", label: "Forecasts", icon: Activity, hint: "RTM/DAM price forecasts" },
      ],
    },
    {
      id: "research",
      label: "Research",
      items: [
        { to: "/trade/backtests", label: "Backtests", icon: FlaskConical, hint: "Blind walk-forward forecast accuracy" },
        { to: "/trade/analytics", label: "Analytics", icon: BarChart3, hint: "Model & forecast accuracy" },
      ],
    },
  ],
};

const COMPLY: ConsoleApp = {
  id: "comply",
  name: "Compliance",
  fullName: "PowerBESS Compliance",
  tagline: "DSM, grid interface & audit",
  icon: Scale,
  accent: "#60a5fa",
  home: "/comply",
  owns: ["/comply"],
  kind: "product",
  groups: [
    {
      id: "overview",
      label: "Overview",
      items: [
        { to: "/comply", label: "Compliance", icon: BadgeCheck, end: true, hint: "DSM exposure & status" },
      ],
    },
    {
      id: "records",
      label: "Records",
      items: [
        { to: "/comply/assessments", label: "DSM Assessments", icon: FileText },
        { to: "/comply/reconciliation", label: "Reconciliation", icon: Waypoints },
        { to: "/comply/audit-chain", label: "Audit Chain", icon: ScrollText },
      ],
    },
    {
      id: "filings",
      label: "Grid & filings",
      items: [
        { to: "/comply/scheduling", label: "Scheduling (SLDC)", icon: ListChecks },
        { to: "/comply/export", label: "Regulator Export", icon: FileText },
      ],
    },
  ],
};

const HEALTH: ConsoleApp = {
  id: "health",
  name: "Health",
  fullName: "PowerBESS Health",
  tagline: "Battery health & hardware life",
  icon: HeartPulse,
  accent: "#34d399",
  home: "/health/telemetry",
  owns: ["/health"],
  kind: "product",
  groups: [
    {
      id: "monitoring",
      label: "Monitoring",
      items: [
        { to: "/health/telemetry", label: "Telemetry", icon: Activity },
        { to: "/health/connectors", label: "Connectors", icon: Waypoints, hint: "Site agent health & enrollment" },
        { to: "/health/trust", label: "Data trust", icon: BadgeCheck, hint: "Dead meters, wrong units, bad config — with fixes" },
      ],
    },
    {
      id: "lifecycle",
      label: "Lifecycle",
      items: [
        { to: "/health/battery", label: "Battery health", icon: BatteryMedium, hint: "SoH vs warranty, weak cells, container & rack health" },
        { to: "/health/degradation", label: "Degradation", icon: BarChart3 },
      ],
    },
  ],
};

const ASSET: ConsoleApp = {
  id: "asset",
  name: "Asset",
  fullName: "PowerBESS Asset",
  tagline: "Operations, safety & maintenance",
  icon: BatteryCharging,
  accent: "#38bdf8",
  home: "/asset",
  owns: ["/asset"],
  kind: "product",
  groups: [
    {
      id: "workspace",
      label: "Workspace",
      items: [
        { to: "/asset", label: "Control room", icon: MonitorPlay, end: true, hint: "Big-screen workspace — open panels side by side" },
      ],
    },
    {
      id: "views",
      label: "Views",
      items: [
        { to: "/asset/fleet", label: "Plant & fleet", icon: Network, hint: "Blocks, PCS fleet, container map, weak cells" },
        { to: "/asset/dispatch", label: "Dispatch & DSM", icon: IndianRupee, hint: "Headroom, set-point check, deviation causes, DSM ₹" },
        { to: "/asset/contract", label: "Contract & LDs", icon: Gavel, hint: "Monthly availability / RTE settlement and LDs" },
        { to: "/asset/operations", label: "Operations", icon: Activity, hint: "Energy, guarantees, actual vs plan, losses" },
        { to: "/asset/safety", label: "Safety", icon: Siren, hint: "Container map, thermal, HVAC & fire, trips" },
        { to: "/asset/maintenance", label: "Maintenance", icon: Wrench, hint: "Ageing vs warranty, rack & equipment health" },
        { to: "/asset/flags", label: "Flags & rules", icon: Flag, hint: "Routed flags with evidence" },
      ],
    },
    {
      id: "monitoring",
      label: "Monitoring",
      items: [
        { to: "/asset/alerts", label: "Alerts", icon: Bell, hint: "Active alerts & rules" },
      ],
    },
    {
      id: "field",
      label: "Field",
      items: [
        { to: "/asset/tickets", label: "Tickets", icon: ClipboardList, hint: "Site work orders for field teams" },
      ],
    },
    {
      id: "fleet",
      label: "Fleet",
      items: [
        { to: "/asset/sites", label: "Assets", icon: Cpu, hint: "BESS fleet & config" },
        { to: "/asset/coverage", label: "Data & coverage", icon: Database, hint: "Signals each site exposes" },
      ],
    },
  ],
};

const DATA: ConsoleApp = {
  id: "data",
  name: "Data",
  fullName: "PowerBESS Data",
  tagline: "Market & grid intelligence feeds",
  icon: Database,
  accent: "#a78bfa",
  home: "/data",
  owns: ["/data"],
  kind: "product",
  groups: [
    {
      id: "overview",
      label: "Overview",
      items: [
        { to: "/data", label: "Feed health", icon: Database, end: true, hint: "Live ingestion status & freshness" },
      ],
    },
    {
      id: "catalog",
      label: "Catalog",
      items: [
        { to: "/data/market", label: "Market Data", icon: CandlestickChart, hint: "IEX cleared prices by delivery date" },
        { to: "/data/grid", label: "Grid Data", icon: Activity, hint: "Frequency & SLDC net-load" },
        { to: "/data/settlement", label: "Settlement", icon: Gauge, hint: "SEM meter blocks & revisions" },
        { to: "/data/exports", label: "Exports", icon: FileText, hint: "CSV snapshots, generated in-browser" },
      ],
    },
  ],
};

const MANAGE: ConsoleApp = {
  id: "manage",
  name: "Organisation",
  fullName: "Organisation & Settings",
  tagline: "Members, roles, audit & account",
  icon: Building2,
  accent: "#f59e0b",
  home: "/org",
  owns: ["/org", "/admin", "/settings"],
  kind: "manage",
  groups: [
    {
      id: "organization",
      label: "Organisation",
      items: [
        { to: "/org", label: "Overview", icon: Building2, end: true, hint: "Org profile & usage" },
        { to: "/org/members", label: "Members", icon: Users, roles: ["admin", "platform_admin"], hint: "Users & invitations" },
        { to: "/org/roles", label: "Roles & Access", icon: KeyRound, roles: ["admin", "platform_admin"] },
      ],
    },
    {
      id: "governance",
      label: "Governance",
      items: [
        { to: "/org/audit", label: "Audit Log", icon: ScrollText, roles: ["admin", "platform_admin", "executive"] },
      ],
    },
    {
      id: "platform",
      label: "Platform",
      items: [
        { to: "/admin/tenants", label: "Tenants", icon: Building2, roles: ["platform_admin"] },
      ],
    },
    {
      id: "personal",
      label: "Personal",
      items: [
        { to: "/settings", label: "Account Settings", icon: UserCog, hint: "Profile, MFA & preferences" },
      ],
    },
  ],
};

// Product order is shared by the launcher tiles, the sidebar "Jump to" list,
// the product switcher (⌘1–⌘7) and the command palette.
export const CONSOLE_APPS: ConsoleApp[] = [OVERVIEW, TRADE, ASSET, HEALTH, COMPLY, DATA, MANAGE];

// Overview's "Jump to" lists the products themselves — real names, icons and
// accents — so the summary is one click from every product home.
OVERVIEW.groups.push({
  id: "products",
  label: "Jump to",
  items: [TRADE, ASSET, HEALTH, COMPLY, DATA].map((a) => ({
    to: a.home,
    label: a.name,
    icon: a.icon,
    accent: a.accent,
    end: true,
    hint: a.tagline,
  })),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasAnyRole(roles: string[], required?: string[]): boolean {
  if (!required || required.length === 0) return true;
  return required.some((r) => roles.includes(r));
}

/** The app that owns the current route (longest matching prefix; Trade default). */
export function appForPath(pathname: string): ConsoleApp {
  let best: ConsoleApp = TRADE;
  let bestLen = 0;
  for (const app of CONSOLE_APPS) {
    for (const base of app.owns) {
      const matches = pathname === base || pathname.startsWith(`${base}/`);
      if (matches && base.length > bestLen) {
        best = app;
        bestLen = base.length;
      }
    }
  }
  return best;
}

/** Apps visible to the given roles, for the launcher. */
export function visibleApps(roles: string[]): ConsoleApp[] {
  return CONSOLE_APPS.filter((app) => hasAnyRole(roles, app.roles));
}

/** Nav groups for an app filtered to the items the roles can see. */
export function visibleGroups(app: ConsoleApp, roles: string[]): ConsoleNavGroup[] {
  return app.groups
    .map((group) => ({ ...group, items: group.items.filter((i) => hasAnyRole(roles, i.roles)) }))
    .filter((group) => group.items.length > 0);
}

export interface PaletteEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  appName: string;
  hint?: string;
  soon?: boolean;
}

/** Flat list of every navigable destination the roles can reach (for search). */
export function allNavEntries(roles: string[]): PaletteEntry[] {
  const out: PaletteEntry[] = [];
  for (const app of visibleApps(roles)) {
    for (const group of visibleGroups(app, roles)) {
      for (const item of group.items) {
        out.push({
          to: item.to,
          label: item.label,
          icon: item.icon,
          appName: app.name,
          hint: item.hint,
          soon: item.soon,
        });
      }
    }
  }
  return out;
}
